/* eslint-disable react/prop-types */
/**
 * OcrProcessingStrip — inline status banner that shows live progress of
 * background OCR for sales documents on a customer. Iter 234.
 *
 *  • Polls the sales-documents list every 4s while ≥1 doc has
 *    `ocr_status` of "pending" or "processing".
 *  • Surfaces the count "N of M processing" + a thin progress bar.
 *  • Dispatches `salesDocs:refresh` so the parent customer page can
 *    refresh its document arrays and pick up newly filled-in titles /
 *    amounts.
 *  • Trust Contract: inline (no modal), no extra wrapping card —
 *    integrates as a single thin row at the top of the Account block.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "../../lib/api";

function pendingCount(docs) {
  return (Array.isArray(docs) ? docs : []).filter(
    (d) => d?.ocr_status === "pending" || d?.ocr_status === "processing",
  ).length;
}

export default function OcrProcessingStrip({ customerId, initialDocs = [], apiPath, label = "documents" }) {
  const [docs, setDocs] = useState(initialDocs);
  const timer = useRef(null);

  useEffect(() => {
    setDocs(initialDocs);
  }, [initialDocs]);

  useEffect(() => {
    if (!customerId && !apiPath) return undefined;
    const stillPending = pendingCount(docs) > 0;
    if (!stillPending) {
      clearTimeout(timer.current);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      try {
        const url = apiPath || `/sales-documents?customer_id=${customerId}`;
        const { data } = await api.get(url);
        const next = Array.isArray(data) ? data : [];
        setDocs(next);
        window.dispatchEvent(
          new CustomEvent("salesDocs:refresh", { detail: { docs: next } }),
        );
      } catch {
        // Silent fail — next tick will retry.
      }
    }, 4000);
    return () => clearTimeout(timer.current);
  }, [customerId, apiPath, docs]);

  const pending = pendingCount(docs);
  if (pending === 0) return null;

  const inBatch = (Array.isArray(docs) ? docs : []).filter((d) => d?.ocr_status).length;
  const done = Math.max(0, inBatch - pending);
  const pct = inBatch > 0 ? Math.round((done / inBatch) * 100) : 0;

  return (
    <div
      data-testid="ocr-processing-strip"
      className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-3 py-2 flex items-center gap-2 text-[11px]"
    >
      <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-300 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-cyan-200 truncate">
          Reading {pending === 1 ? `1 ${label.replace(/s$/, "")}` : `${pending} ${label}`} —
          {" "}
          <span className="text-[#a0aabf]">titles, numbers &amp; amounts fill in here as they finish.</span>
        </div>
        {inBatch > 1 && (
          <div className="mt-1 h-1 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-cyan-400 transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>
      <span className="text-cyan-300 font-mono tabular-nums shrink-0">
        {done}/{inBatch}
      </span>
    </div>
  );
}
